/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { CreateConversationRequest } from '../models/create-conversation-request';
import { CreateConversationResponse } from '../models/create-conversation-response';


/**
 * Create and manage conversations in CosmosDB
 */
@Injectable({
  providedIn: 'root',
})
export class ConversationManagementApiService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation createConversation
   */
  static readonly CreateConversationPath = '/langgraph/conversation/create';

  /**
   * Create a new conversation in CosmosDB.
   *
   * Creates a new conversation record in CosmosDB and returns a conversation_id
   * for subsequent message/state persistence.
   *
   * **Client Usage** (`langgraph_client.py` line 568-638):
   * ```python
   * async def _create_conversation(self, title: str) -> Optional[str]:
   *     body = {"title": title}
   *     response = await client.post(
   *         f"{self.base_url}/langgraph/conversation/create",
   *         json=body,
   *         headers=headers
   *     )
   *     result = response.json()
   *     self.conversation_id = result.get("conversation_id")
   * ```
   *
   * **When to Call**: Before sending the first message in a new conversation.
   * The client automatically calls this on the first `send_message()`.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createConversation()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createConversation$Response(params: {
    context?: HttpContext
    body: CreateConversationRequest
  }
): Observable<StrictHttpResponse<CreateConversationResponse>> {

    const rb = new RequestBuilder(this.rootUrl, ConversationManagementApiService.CreateConversationPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<CreateConversationResponse>;
      })
    );
  }

  /**
   * Create a new conversation in CosmosDB.
   *
   * Creates a new conversation record in CosmosDB and returns a conversation_id
   * for subsequent message/state persistence.
   *
   * **Client Usage** (`langgraph_client.py` line 568-638):
   * ```python
   * async def _create_conversation(self, title: str) -> Optional[str]:
   *     body = {"title": title}
   *     response = await client.post(
   *         f"{self.base_url}/langgraph/conversation/create",
   *         json=body,
   *         headers=headers
   *     )
   *     result = response.json()
   *     self.conversation_id = result.get("conversation_id")
   * ```
   *
   * **When to Call**: Before sending the first message in a new conversation.
   * The client automatically calls this on the first `send_message()`.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createConversation$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createConversation(params: {
    context?: HttpContext
    body: CreateConversationRequest
  }
): Observable<CreateConversationResponse> {

    return this.createConversation$Response(params).pipe(
      map((r: StrictHttpResponse<CreateConversationResponse>) => r.body as CreateConversationResponse)
    );
  }

}
